"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StackTypeControlGroup = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importDefault(require("react"));
const react_redux_1 = require("react-redux");
const react_core_1 = require("@patternfly/react-core");
const formik_1 = require("formik");
const ip_address_1 = require("ip-address");
const common_1 = require("../../../../common");
const ui_1 = require("../../../../common/components/ui");
const ClusterDefaultConfigurationContext_1 = require("../ClusterDefaultConfigurationContext");
const selectors_1 = require("../../../store/slices/current-cluster/selectors");
const OcmFormFields_1 = require("../../ui/OcmFormFields");
const hasDualStackConfigurationChanged = (clusterNetworks, serviceNetworks, cidrIPv6, values) => clusterNetworks &&
    serviceNetworks &&
    ((values.machineNetworks && values.machineNetworks[1].cidr !== cidrIPv6) ||
        (values.clusterNetworks && values.clusterNetworks[1].cidr !== clusterNetworks[1].cidr) ||
        (values.clusterNetworks &&
            values.clusterNetworks[1].hostPrefix !== clusterNetworks[1].hostPrefix) ||
        (values.serviceNetworks && values.serviceNetworks[1].cidr !== serviceNetworks[1].cidr));
const StackTypeControlGroup = ({ clusterId, isDualStackSelectable, hostSubnets, }) => {
    const { setFieldValue, values, validateForm } = (0, formik_1.useFormikContext)();
    const [openConfirmModal, setConfirmModal] = react_1.default.useState(false);
    const defaultNetworkValues = (0, ClusterDefaultConfigurationContext_1.useDefaultConfiguration)([
        'clusterNetworksDualstack',
        'clusterNetworksIpv4',
        'serviceNetworksDualstack',
        'serviceNetworksIpv4',
    ]);
    const IPv6Subnets = hostSubnets.filter((subnet) => ip_address_1.Address6.isValid(subnet.subnet));
    const cidrIPv6 = IPv6Subnets.length >= 1 ? IPv6Subnets[0].subnet : common_1.NO_SUBNET_SET;
    const { isViewerMode } = (0, react_redux_1.useSelector)(selectors_1.selectCurrentClusterPermissionsState);
    const shouldSetSingleStack = !isViewerMode && !isDualStackSelectable && values.stackType === common_1.DUAL_STACK;
    const setSingleStack = react_1.default.useCallback(() => {
        var _a, _b, _c;
        setFieldValue('stackType', common_1.IPV4_STACK);
        if (values.machineNetworks && ((_a = values.machineNetworks) === null || _a === void 0 ? void 0 : _a.length) >= 2) {
            setFieldValue('machineNetworks', values.machineNetworks.slice(0, 1));
        }
        if (values.clusterNetworks && ((_b = values.clusterNetworks) === null || _b === void 0 ? void 0 : _b.length) >= 2) {
            setFieldValue('clusterNetworks', values.clusterNetworks.slice(0, 1));
        }
        if (values.serviceNetworks && ((_c = values.serviceNetworks) === null || _c === void 0 ? void 0 : _c.length) >= 2) {
            setFieldValue('serviceNetworks', values.serviceNetworks.slice(0, 1));
        }
        void validateForm();
    }, [
        setFieldValue,
        validateForm,
        values.clusterNetworks,
        values.machineNetworks,
        values.serviceNetworks,
    ]);
    const setDualStack = () => {
        var _a, _b, _c, _d, _e;
        setFieldValue('stackType', common_1.DUAL_STACK);
        setFieldValue('vipDhcpAllocation', false);
        if (values.networkType === common_1.NETWORK_TYPE_SDN) {
            setFieldValue('networkType', common_1.NETWORK_TYPE_OVN);
        }
        if (values.machineNetworks && ((_a = values.machineNetworks) === null || _a === void 0 ? void 0 : _a.length) < 2) {
            setFieldValue('machineNetworks', [...values.machineNetworks, { cidr: cidrIPv6, clusterId: clusterId }], false);
        }
        if (values.clusterNetworks && ((_b = values.clusterNetworks) === null || _b === void 0 ? void 0 : _b.length) < 2) {
            setFieldValue('clusterNetworks', [
                ...values.clusterNetworks,
                ((_c = defaultNetworkValues.clusterNetworksDualstack) === null || _c === void 0 ? void 0 : _c.length)
                    ? defaultNetworkValues.clusterNetworksDualstack[1]
                    : { cidr: '', hostPrefix: '', clusterId: clusterId },
            ], false);
        }
        if (values.serviceNetworks && ((_d = values.serviceNetworks) === null || _d === void 0 ? void 0 : _d.length) < 2) {
            setFieldValue('serviceNetworks', [
                ...values.serviceNetworks,
                ((_e = defaultNetworkValues.serviceNetworksDualstack) === null || _e === void 0 ? void 0 : _e.length)
                    ? defaultNetworkValues.serviceNetworksDualstack[1]
                    : { cidr: '', clusterId: clusterId },
            ], false);
        }
    };
    const setStackType = (e) => {
        if (e.target.value === common_1.DUAL_STACK) {
            setDualStack();
        }
        else if (hasDualStackConfigurationChanged(defaultNetworkValues.clusterNetworksDualstack || [], defaultNetworkValues.serviceNetworksDualstack || [], cidrIPv6, values)) {
            setConfirmModal(true);
        }
        else {
            setSingleStack();
        }
    };
    react_1.default.useEffect(() => {
        if (shouldSetSingleStack) {
            setSingleStack();
        }
    }, [shouldSetSingleStack, setSingleStack]);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(react_core_1.FormGroup, { label: "Networking stack type", fieldId: (0, common_1.getFieldId)('stackType', 'radio'), onChange: setStackType, isInline: true },
            react_1.default.createElement(react_core_1.Split, null,
                react_1.default.createElement(react_core_1.SplitItem, null,
                    react_1.default.createElement(OcmFormFields_1.OcmRadioField, { name: 'stackType', value: common_1.IPV4_STACK, isDisabled: !isDualStackSelectable, label: "IPv4\u00A0" })),
                react_1.default.createElement(react_core_1.SplitItem, null,
                    react_1.default.createElement(ui_1.PopoverIcon, { bodyContent: "Select this when your hosts are using only IPv4.", buttonStyle: { top: '-4px' } }))),
            react_1.default.createElement(react_core_1.Split, null,
                react_1.default.createElement(react_core_1.SplitItem, null,
                    react_1.default.createElement(react_core_1.Tooltip, { content: 'Dual-stack is only available when your hosts are using IPV4 together with IPV6.', hidden: isDualStackSelectable },
                        react_1.default.createElement(OcmFormFields_1.OcmRadioField, { name: 'stackType', value: common_1.DUAL_STACK, isDisabled: !isDualStackSelectable, label: "Dual-stack\u00A0" }))),
                react_1.default.createElement(react_core_1.SplitItem, null,
                    react_1.default.createElement(ui_1.PopoverIcon, { bodyContent: "Select dual-stack when your hosts are using IPV4 together with IPV6.", buttonStyle: { top: '-4px' } })))),
        openConfirmModal && (react_1.default.createElement(ui_1.ConfirmationModal, { title: 'Change stack type?', titleIconVariant: 'warning', confirmationButtonText: 'Change', confirmationButtonVariant: react_core_1.ButtonVariant.primary, content: react_1.default.createElement("p", null, "All data and configuration done for 'Dual-stack' will be lost."), onClose: () => {
                setConfirmModal(false);
                setDualStack();
            }, onConfirm: () => {
                setConfirmModal(false);
                setSingleStack();
            } }))));
};
exports.StackTypeControlGroup = StackTypeControlGroup;
//# sourceMappingURL=StackTypeControl.js.map